import { useState } from "react";
import axios from "axios";
import { PROJECT_TYPES, CONTACT } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project_type: PROJECT_TYPES[0],
  location: "",
  details: "",
};

export default function RequestQuote() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.project_type) {
      setStatus({ state: "error", message: "Please complete name, email and project type." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: "request-quote" });
      setStatus({
        state: "success",
        message: "Thank you. A senior from the studio will respond within one working day.",
      });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (detail || "Something went wrong. Please try again or call us directly.");
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <div data-testid="request-quote-page" className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-cream">
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 sm:gap-8 mb-8 sm:mb-10 md:mb-14">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono-label text-stone-text">Request a sample</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl leading-[1.0]">
              Tell us about<br />
              the <em className="italic text-terracotta">project</em>.
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg max-w-2xl text-stone-text leading-relaxed">
              Share the surface, the dimension, and the intent. A senior from the studio will respond within one working day with the right next step — a sample, a site visit, or a quote.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-10 sm:gap-12 border-t border-warm pt-10 sm:pt-16">
          <aside className="col-span-12 md:col-span-4 space-y-8 sm:space-y-10">
            <div>
              <p className="font-mono-label text-stone-text mb-3">Direct line</p>
              <a
                href={CONTACT.phoneHref}
                data-testid="quote-direct-phone"
                className="font-serif-display text-xl sm:text-2xl md:text-3xl hover:text-terracotta break-all"
              >
                {CONTACT.phone}
              </a>
            </div>
            <div>
              <p className="font-mono-label text-stone-text mb-3">Studio email</p>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="quote-direct-email"
                className="font-serif-display text-lg sm:text-xl md:text-2xl hover:text-terracotta break-all"
              >
                {CONTACT.email}
              </a>
            </div>
            <div className="text-stone-text text-xs sm:text-sm leading-relaxed">
              {CONTACT.address.map((l) => <p key={l}>{l}</p>)}
            </div>
          </aside>

          <form
            data-testid="quote-form"
            onSubmit={onSubmit}
            className="col-span-12 md:col-span-8 grid grid-cols-2 gap-x-5 sm:gap-x-8 gap-y-5 sm:gap-y-6"
          >
            <div className="col-span-2 md:col-span-1">
              <label className="tz-label" htmlFor="name">Name *</label>
              <input
                data-testid="quote-input-name" id="name" name="name" value={form.name}
                onChange={onChange} required className="tz-input" placeholder="Your full name"
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="tz-label" htmlFor="company">Company / Studio</label>
              <input
                data-testid="quote-input-company" id="company" name="company" value={form.company}
                onChange={onChange} className="tz-input" placeholder="Optional"
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <label className="tz-label" htmlFor="email">Email *</label>
              <input
                data-testid="quote-input-email" id="email" name="email" type="email" required
                value={form.email} onChange={onChange} className="tz-input" placeholder="you@studio.com"
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="tz-label" htmlFor="phone">Phone</label>
              <input
                data-testid="quote-input-phone" id="phone" name="phone" value={form.phone}
                onChange={onChange} className="tz-input" placeholder="+91 ..."
              />
            </div>

            <div className="col-span-2 md:col-span-1">
              <label className="tz-label" htmlFor="project_type">Project type</label>
              <select
                data-testid="quote-input-projecttype" id="project_type" name="project_type"
                value={form.project_type} onChange={onChange} className="tz-input"
              >
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="tz-label" htmlFor="location">Project location (city)</label>
              <input
                data-testid="quote-input-location" id="location" name="location"
                value={form.location} onChange={onChange} className="tz-input" placeholder="Mumbai, Pune, Goa..."
              />
            </div>

            <div className="col-span-2">
              <label className="tz-label" htmlFor="details">Details</label>
              <textarea
                data-testid="quote-input-details" id="details" name="details" rows={5}
                value={form.details} onChange={onChange} className="tz-input resize-none"
                placeholder="Surface area, aggregate preferences, references, timeline..."
              />
            </div>

            <div className="col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-3 sm:mt-4">
              <div className="text-sm min-h-[1.5rem] order-2 md:order-1">
                {status.state === "success" && (
                  <p data-testid="quote-success" className="text-terracotta">{status.message}</p>
                )}
                {status.state === "error" && (
                  <p data-testid="quote-error" className="text-red-700">{status.message}</p>
                )}
              </div>
              <button
                data-testid="quote-submit"
                type="submit"
                disabled={status.state === "loading"}
                className="font-mono-label px-6 sm:px-8 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors disabled:opacity-50 w-full md:w-auto order-1 md:order-2"
              >
                {status.state === "loading" ? "Sending..." : "Send Enquiry →"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
